// Single source of truth for the worm-color palette. Core ships 30 hand-picked
// colors (enough for a full Royale room); big local games want many more. The renderers resolve a worm's color by id
// in core's shared `wormColors` map, so any colorId we use MUST be registered
// there. We extend that map IN PLACE (idempotently) with a batch of hand-picked
// colors plus generated ones, guaranteeing at least TARGET distinct ids.
//
// Importing this module anywhere installs the full palette, so it no longer
// matters whether the online lobby was visited first (it used to do this itself).
import { wormColors, wormColorIds } from "core/src/core/constants.js";

// At least this many distinct colors must exist: comfortably more than the local
// worm cap (40), plus spares and variety so adding a player always finds a free color.
const TARGET = 48;

// Hand-picked extras. Core now ships all of these itself (same ids, same order),
// so registering them here is a no-op kept as a guard against an older core.
const EXTRA_COLORS: Record<string, string> = {
  red: "#F44336",
  cyan: "#00E5FF",
  yellow: "#FFEE58",
  magenta: "#E040FB",
  sky: "#40C4FF",
  coral: "#FF7043",
  mint: "#1DE9B6",
  gold: "#FFD740",
  violet: "#7C4DFF",
  rose: "#FF4081",
  white: "#FAFAFA",
  chartreuse: "#8EE000",
  azure: "#2979FF",
  lavender: "#C9A6FF",
  orchid: "#FF8AD8",
  salmon: "#FF8A80",
  tan: "#C49A6C",
  olive: "#9E9D24",
  seafoam: "#A8F0C8",
  plum: "#B06A9E",
};

function hslToHex(h: number, s: number, l: number): string {
  const a = (s * Math.min(l, 100 - l)) / 100 / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const c = l / 100 - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * c)
      .toString(16)
      .padStart(2, "0");
  };
  return "#" + f(0) + f(8) + f(4);
}

function register(id: string, hex: string) {
  if (!(wormColors as any)[id]) {
    (wormColors as any)[id] = hex;
    (wormColorIds as string[]).push(id);
  }
}

for (const id of Object.keys(EXTRA_COLORS)) register(id, EXTRA_COLORS[id]);

// Top up with evenly-spread generated hues (golden-angle) until we hit TARGET.
let g = 0;
while ((wormColorIds as string[]).length < TARGET) {
  const hue = Math.round((g * 137.508) % 360); // golden angle → good spread
  register("gen" + g, hslToHex(hue, 68, 56));
  g++;
}

// Snapshot of all color ids (core's 30 + generated). Safe to use for
// pickers, color assignment and player caps.
export const WORM_COLOR_IDS: string[] = (wormColorIds as string[]).slice();

// Give every colour id a real colour before it is drawn. An id the palette
// doesn't know (a stale save, a colour from an older or newer build) would
// otherwise draw as no colour at all: the canvas keeps the last colour it used,
// so the trail came out black, then in other players' colours. Such an id is
// mapped onto one of core's colours, the same one every time.
export function ensureColors(players: Array<{ colorId?: string | null }> | null | undefined): void {
  for (const p of players || []) {
    const id = p && p.colorId;
    if (!id || (wormColors as any)[id]) continue;
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
    const base = (wormColorIds as string[])[Math.abs(h) % 30];
    (wormColors as any)[id] = (wormColors as any)[base];
  }
}
